export interface NoteArticle {
  slug: string;
  category: string;
  categoryLabel: string;
  title: string;
  excerpt: string;
  readTime: number;
  publishedAt: string;
  coverImage: string;
  tags: string[];
  isFeatured?: boolean;
  seoKeywords?: string[];
  content: {
    intro: string;
    sections: { heading: string; body: string }[];
    closing: string;
  };
}

export const noteCategories = [
  { id: 'beginner', label: '初學入門', description: '沒有基礎也可以開始的地方', seoDescription: '「初學入門」精選古箏入門文章，從沒有音樂基礎、成人初學、孩子幾歲開始，到第一次接觸古箏的準備，鳴箏老師陪你安心踏出第一步。', icon: 'ri-seedling-line', color: '#7a9e7e', articles: 5 },
  { id: 'learning', label: '學習陪伴', description: '慢慢來，也是一種前進', seoDescription: '「學習陪伴」分享古箏學習節奏、練習心態與陪伴式學習，談慢學、沒時間練習、安心學習，讓音樂溫柔地進入日常。', icon: 'ri-heart-line', color: '#b08060', articles: 4 },
  { id: 'guzheng-guide', label: '選箏指南', description: '找到真正適合你的那把箏', seoDescription: '「選箏指南」教你如何挑選古箏，從初學者選箏、價位迷思、租借或購買的抉擇，找到真正適合你的那一把箏。', icon: 'ri-music-2-line', color: '#7a8b6e', articles: 4 },
  { id: 'faq', label: '常見問題', description: '那些你想問又不確定怎麼問的事', seoDescription: '「常見問題」整理古箏學習與課程的常見疑問，加入 LINE 是否要報名、練習頻率、大人與孩子的學習差異，一次為你解答。', icon: 'ri-question-line', color: '#9a8060', articles: 3 },
  { id: 'teacher-notes', label: '老師筆記', description: '鳴箏日常・教學現場的細碎感受', seoDescription: '「老師筆記」收錄鳴箏老師的教學觀點與學習分享，談陪伴式教學、音樂與生活，以及看見學生改變的感動。', icon: 'ri-quill-pen-line', color: '#6e7a5e', articles: 4 },
];

export const noteArticles: NoteArticle[] = [
  {
    slug: 'no-music-background-can-learn-guzheng',
    category: 'beginner',
    categoryLabel: '初學入門',
    title: '沒有音樂基礎，也可以學古箏嗎？',
    excerpt: '很多人在第一次詢問時，都會先說「我完全沒學過音樂」，好像這是一件需要道歉的事。但其實，對古箏來說，空白反而是最好的起點。',
    readTime: 6,
    publishedAt: '2025-03-10',
    isFeatured: true,
    coverImage: 'https://storage.helloreaddy.io/project_files/79c1f631-1cb7-456b-a6cf-fdd8ff21e9a0/67b47f79-c872-4107-802e-ea9eac84b482_compressed_C--Image-202695-04_21_49.webp',
    tags: ['古箏初學', '沒有音樂基礎', '成人學古箏'],
    seoKeywords: ['沒有音樂基礎學古箏', '古箏初學者', '成人學古箏'],
    content: {
      intro: '很多人在第一次詢問時，都會先說「我完全沒學過樂器」，好像這是一件需要先解釋的事。但我想告訴你——對古箏來說，空白反而是最好的起點。',
      sections: [
        { heading: '古箏不需要你「已經懂音樂」', body: '古箏是一種五聲音階樂器，它的音色天生就帶著和諧感。即使你對樂理一無所知，彈出的第一個音也不會讓人皺眉。五聲音階的結構讓任何兩個音放在一起都能和諧共存，這也是為什麼很多人第一堂課就能彈出有旋律感的片段。' },
        { heading: '「沒有基礎」其實是一種優勢', body: '學過鋼琴或其他樂器的學生，有時反而需要先放掉一些慣性。對完全空白的初學者而言，沒有需要覆蓋的舊習慣，反而能更快進入古箏自己的語言。' },
        { heading: '我們從「聽聲音」開始，不從記譜開始', body: '在鳴箏的教學裡，初學階段我們不急著讓你認識所有符號或背下指法名稱。我們先從讓你感受這個樂器的聲音開始，當你開始對聲音有感覺，學習就會從「努力完成任務」變成「想繼續靠近這個東西」。' },
        { heading: '什麼時候可以開始？', body: '現在就可以。你不需要準備什麼，不需要先買樂器，也不需要先學樂理。第一次可以先來聊聊你對音樂的感受，說「我就是想試試看」也完全可以。' },
      ],
      closing: '如果你也在想著「我這樣可以嗎」，答案是——可以。加入 LINE@ 跟我聊聊，不用準備，不用決定，只要把你的問題帶過來就好。',
    },
  },
  {
    slug: 'adult-learn-guzheng-too-late',
    category: 'beginner',
    categoryLabel: '初學入門',
    title: '成人現在開始學古箏，會太晚嗎？',
    excerpt: '「我已經三十多歲了，還來得及嗎？」——這是很多成人學員問我的第一個問題。我想認真回答這個問題，因為它背後藏著太多不必要的自我懷疑。',
    readTime: 7,
    publishedAt: '2025-03-18',
    isFeatured: false,
    coverImage: 'https://storage.helloreaddy.io/project_files/79c1f631-1cb7-456b-a6cf-fdd8ff21e9a0/d2deaa05-24e2-4c1f-b4b9-7823b570d5dd_compressed_8c0ad421-c469-4e16-b96f-89536cfc1269.webp',
    tags: ['成人學古箏', '幾歲開始學古箏', '古箏適合成人嗎'],
    seoKeywords: ['成人學古箏', '幾歲可以學古箏', '大人學古箏會太晚嗎'],
    content: {
      intro: '你問這個問題，代表你心裡還想著古箏。那就夠了。這篇文章想認真回答這個「太晚」的問題。',
      sections: [
        { heading: '「太晚」這件事，是對誰說的？', body: '如果你學古箏不是為了比賽、不是為了考級，那「晚」這個詞就沒有位置了。你只是在某一個你準備好的時刻，選擇讓音樂進入生活。' },
        { heading: '成人學習的優勢是你不知道的', body: '成人有更強的專注力，更清楚自己想要什麼，也更有能力感受音樂的細節。每一次練習你都是有意識地選擇坐下來，這種主動性讓學習更有深度。' },
        { heading: '手指的靈活度，沒有你想的那麼關鍵', body: '古箏的基礎技法並不需要極高的手指靈活度。初學階段，需要的是手指的正確施力方式和對聲音的敏感度，這兩件事成人其實做得比孩子更好。' },
        { heading: '你唯一需要的，是一個自己想要開始的理由', body: '也許是因為小時候看過別人彈很嚮往、也許只是想試試看。這些理由都夠了。' },
      ],
      closing: '如果你心裡有一個「也許可以試試」的感覺，那就是開始的時候了。加入 LINE@ 聊聊你的狀態。',
    },
  },
  {
    slug: 'is-guzheng-suitable-for-children',
    category: 'beginner',
    categoryLabel: '初學入門',
    title: '孩子適合學古箏嗎？什麼年紀可以開始？',
    excerpt: '很多家長希望讓孩子接觸音樂，卻擔心選錯樂器、選錯時機、選錯老師。關於孩子與古箏，這裡有一些我想分享的觀察。',
    readTime: 7,
    publishedAt: '2025-03-25',
    isFeatured: false,
    coverImage: 'https://storage.helloreaddy.io/project_files/79c1f631-1cb7-456b-a6cf-fdd8ff21e9a0/c1c5e4b0-176c-439a-ab75-665014fd55ea_compressed_C--Image-202695-04_50_30.webp',
    tags: ['孩子學古箏', '兒童古箏', '幾歲學古箏'],
    seoKeywords: ['孩子適合學古箏嗎', '兒童學古箏年齡', '古箏幾歲開始學'],
    content: {
      intro: '我想從一個老師的角度，分享一些關於孩子與古箏的真實觀察。不是告訴你「古箏適合所有孩子」，而是幫你判斷古箏是否適合你的孩子。',
      sections: [
        { heading: '幾歲可以開始？', body: '一般來說，五到六歲是比較合適的起點。但更重要的不是年齡，而是孩子對聲音是否有好奇心，以及家長是否能給予穩定的陪伴支持。' },
        { heading: '古箏對孩子的影響，不只是音樂', body: '除了音樂技能本身，學古箏的過程培養了很多孩子的內在能力：耐心等待聲音、感受細微差異、反覆練習的穩定性。' },
        { heading: '孩子需要的不是「最好的老師」，而是「最安心的環境」', body: '孩子能不能在那個空間裡感到安心，才是真正決定學習效果的關鍵。如果孩子每次上課都有點緊張，那這個學習環境可能需要重新評估。' },
        { heading: '什麼樣的家長，適合讓孩子來這裡學？', body: '如果你希望音樂是孩子生命裡一段溫柔的陪伴，一件讓他有機會安靜下來的事——那這裡非常歡迎你們。' },
      ],
      closing: '有關於孩子學古箏的疑問，都可以加入 LINE@ 聊聊。不需要先有答案，帶著問題來就好。',
    },
  },
  {
    slug: 'what-kind-of-person-suits-guzheng',
    category: 'beginner',
    categoryLabel: '初學入門',
    title: '古箏適合什麼樣的人？來確認看看你是不是那種人',
    excerpt: '不是每一種樂器都適合每一個人。古箏有它自己的氣質，也有它最能陪伴的那一類人。這篇文章不是要說服你來學，而是想幫你認識自己更適合什麼。',
    readTime: 5,
    publishedAt: '2025-04-01',
    isFeatured: false,
    coverImage: 'https://readdy.ai/api/search-image?query=person%20sitting%20quietly%20by%20window%20with%20cup%20of%20tea%20reflecting%20peacefully%2C%20warm%20golden%20afternoon%20light%2C%20minimal%20interior%20with%20soft%20beige%20and%20cream%20tones%2C%20contemplative%20serene%20mood%2C%20lifestyle%20photography&width=800&height=500&seq=note011&orientation=landscape',
    tags: ['古箏適合誰', '適合學古箏的人', '古箏初學'],
    seoKeywords: ['古箏適合什麼人', '適合學古箏嗎'],
    content: {
      intro: '這篇文章不是要說服你學古箏，而是幫你想清楚：你是不是那種會和古箏有默契的人。',
      sections: [
        { heading: '喜歡安靜、或是想為自己找到一段安靜時光的人', body: '古箏的音色是安靜的、有縱深的，它更像是一個讓你往內走的聲音。如果你想為自己保留一段不需要做什麼、只是安靜存在的時間，古箏會是很好的陪伴。' },
        { heading: '不急著「有成果」、願意慢慢感受的人', body: '如果你願意先不問結果，只是讓自己在聲音裡待著，你會發現這個等待是值得的。慢慢學會的東西，往往留得更久。' },
        { heading: '對東方美學或自然音色有感覺的人', body: '如果你對那種帶著空氣感的音色、自然木質的觸感有一種天然的共鳴，古箏很可能是你一直在等的那個東西。' },
        { heading: '不確定自己是不是這種人，也可以來試試', body: '這幾個描述只是參考，不是硬性條件。最準確的方式，是來感受看看，而不是在心裡預測答案。' },
      ],
      closing: '如果你看完這篇文章，心裡有一點「好像說的是我」，加入 LINE@ 告訴我你的狀態。',
    },
  },
  {
    slug: 'first-steps-for-guzheng-beginner',
    category: 'beginner',
    categoryLabel: '初學入門',
    title: '想學古箏，第一步應該怎麼開始？',
    excerpt: '很多人在「想學」和「真的開始」之間卡住了很久。這篇文章想幫你把那個距離縮短一點——告訴你第一步可以是什麼。',
    readTime: 5,
    publishedAt: '2025-04-15',
    isFeatured: false,
    coverImage: 'https://readdy.ai/api/search-image?query=open%20door%20leading%20to%20bright%20warm%20room%20with%20guzheng%20visible%20inside%2C%20threshold%20of%20beginning%20something%20new%2C%20warm%20afternoon%20light%2C%20minimalist%20interior%20with%20wood%20and%20linen%20textures%2C%20hopeful%20calm%20atmosphere&width=800&height=500&seq=note012&orientation=landscape',
    tags: ['古箏初學第一步', '怎麼開始學古箏', '古箏入門'],
    seoKeywords: ['想學古箏從哪開始', '古箏初學第一步'],
    content: {
      intro: '這篇文章想給你一個可以踏出去的具體方向，縮短「想學」到「開始」的距離。',
      sections: [
        { heading: '第一步：讓自己多聽一些古箏的聲音', body: '可以在 YouTube 搜尋「古箏輕音樂」，找幾首讓你想一直播放的。聽的目的不是學習，而是確認你和這個聲音之間有沒有一種自然的吸引力。' },
        { heading: '第二步：不需要先買箏，先找到可以聊的地方', body: '你不需要先買箏，也不需要先學樂理。最好的第一步，是找一個可以問問題、聊聊你的狀態的地方。' },
        { heading: '第三步：試一堂課，感受一下再決定', body: '體驗課的目的不是評比老師的技術，而是讓你感受一下自己在這個學習環境裡的狀態。你離開之後有沒有想再來的感覺？這些感受比任何課程介紹都更重要。' },
        { heading: '開始，不需要等到「準備好了」', body: '你現在的狀態已經夠了。帶著你的問題和猶豫，就可以先來聊聊。' },
      ],
      closing: '最簡單的方式就是加入 LINE@ 跟我說一句：「我想了解看看。」就這樣，其他的我們慢慢來。',
    },
  },
  {
    slug: 'why-slow-learning-matters',
    category: 'learning',
    categoryLabel: '學習陪伴',
    title: '學古箏，可以不用那麼快',
    excerpt: '我們生活在一個什麼都想快的時代。學習語言要速成、學烹飪看短片、學樂器也希望一個月能表演。但古箏告訴我：慢，也是一種到達的方式。',
    readTime: 6,
    publishedAt: '2025-04-02',
    isFeatured: true,
    coverImage: 'https://storage.helloreaddy.io/project_files/79c1f631-1cb7-456b-a6cf-fdd8ff21e9a0/1f60e1c6-6150-4d9f-8090-330ef43af686_compressed_G_Image_7ez5ic7ez5ic7ez5.webp',
    tags: ['慢學古箏', '不要速成', '音樂與生活'],
    seoKeywords: ['學古箏多久學會', '古箏學習節奏'],
    content: {
      intro: '這篇文章想聊聊「快」和「慢」在音樂學習裡分別意味著什麼。',
      sections: [
        { heading: '「快」學到的，有多深？', body: '很多人花三個月學完一首曲子，但問他對這首曲子有什麼感受，他說不太出來——因為他太忙著「學完」，沒有時間「感受」。' },
        { heading: '慢學的人，往往走得更遠', body: '那些真正讓音樂成為生活一部分的人，大多不是進步最快的。他們願意在同一段旋律上停留很久，慢，讓他們把音樂真正放進日常。' },
        { heading: '鳴箏的教學，沒有追趕進度的壓力', body: '在這裡，你不需要擔心自己「落後」。你上次課學到哪裡、你今天想彈什麼——這些才是我們每堂課的起點。' },
        { heading: '音樂是用來陪伴你的，不是你去追它的', body: '音樂本來應該是生活裡的一塊安靜空間，而不是另一項需要達標的任務。' },
      ],
      closing: '如果你也在尋找一種「不那麼有壓力的學習方式」，加入 LINE@ 聊聊你的狀態。',
    },
  },
  {
    slug: 'practice-is-not-homework',
    category: 'learning',
    categoryLabel: '學習陪伴',
    title: '練習不是交作業，而是慢慢靠近聲音',
    excerpt: '很多人把練習想成一種「必須完成的任務」，做完了才能放鬆。但如果練習本身就是放鬆呢？',
    readTime: 6,
    publishedAt: '2025-04-10',
    isFeatured: false,
    coverImage: 'https://storage.helloreaddy.io/project_files/79c1f631-1cb7-456b-a6cf-fdd8ff21e9a0/31c1c416-d12a-4c2a-92ab-ce4e9d70b74a_compressed_C-Image-202695-04_14_07.webp',
    tags: ['古箏練習', '學習態度', '陪伴式學習'],
    seoKeywords: ['古箏練習方法', '輕鬆學古箏'],
    content: {
      intro: '練習已經變成了一種壓力。但我想聊聊另一種可能——讓練習本身成為你一天裡的一個呼吸時刻。',
      sections: [
        { heading: '「練習」這兩個字的問題', body: '我更喜歡說「陪陪它」。就像你去探望一個老朋友，不需要帶什麼目的，只是坐下來、待在一起，就夠了。' },
        { heading: '不是每次都要進步，有時候只是維持連結', body: '哪怕只是坐下來調個弦、彈幾個音，也算是一次靠近。這種低門檻的「靠近」，長期下來比偶爾一次的高強度練習更有效果。' },
        { heading: '練習的重點，不是時間，是品質', body: '二十分鐘專心的練習，遠比一小時心不在焉地彈要有效。你在彈的時候，有沒有真的在聽自己彈出來的聲音？' },
        { heading: '老師的角色，是幫你找到自己的節奏', body: '在鳴箏，我不會給你一張「這週要練這些」的清單。我更在意的是：你上週的感受是什麼？' },
      ],
      closing: '加入 LINE@，告訴我你對練習的感受，不管是期待還是猶豫，都可以說。',
    },
  },
  {
    slug: 'no-time-to-learn-guzheng',
    category: 'learning',
    categoryLabel: '學習陪伴',
    title: '沒有時間，還能學古箏嗎？',
    excerpt: '「我很想學，但我真的很忙。」這是我聽過最多次的句子之一。我想認真回答這個問題，因為「沒時間」可能沒有你以為的那麼絕對。',
    readTime: 6,
    publishedAt: '2025-04-22',
    isFeatured: false,
    coverImage: 'https://storage.helloreaddy.io/project_files/79c1f631-1cb7-456b-a6cf-fdd8ff21e9a0/901b6e53-09be-4b11-b8d8-8935c918aca7_compressed_C--Image-202695-04_40_29.webp',
    tags: ['沒時間學古箏', '忙碌成人學音樂', '古箏學習節奏'],
    seoKeywords: ['沒有時間學古箏', '上班族學古箏'],
    content: {
      intro: '我不想說「沒時間就是不夠想」，因為那不公平。但我想換個角度聊聊「時間」這件事。',
      sections: [
        { heading: '學古箏不需要每天練兩小時', body: '每週一堂課、每次在家自主練習二十到三十分鐘，已經足夠讓你穩定前進了。每三天練二十分鐘，比每週練一次兩小時更有效。' },
        { heading: '忙，有時候反而更需要音樂', body: '越忙的學生，往往越珍惜那一個小時的上課時間。因為那一個小時裡，他們只需要把注意力放在音符和呼吸上。' },
        { heading: '課程節奏可以依生活狀態調整', body: '如果你某段時間特別忙，我們可以把課堂間隔拉長。學習可以配合你的生活，而不是要求你配合它。' },
        { heading: '試著找出你的「空白縫隙」', body: '等咖啡的十分鐘、睡前的十五分鐘——如果你把古箏放進這些縫隙，你會發現它其實是放得進去的。' },
      ],
      closing: '可以加入 LINE@ 跟我聊聊你的生活節奏。我們一起評估什麼樣的方式適合你現在的情況。',
    },
  },
  {
    slug: 'one-safe-learning-is-better-than-fast-progress',
    category: 'learning',
    categoryLabel: '學習陪伴',
    title: '一段安心的學習，比快速進步更重要',
    excerpt: '我在教學裡觀察到一件事：最後真正把古箏留在生活裡的人，不是進步最快的，而是學得最安心的。',
    readTime: 6,
    publishedAt: '2025-05-15',
    isFeatured: false,
    coverImage: 'https://storage.helloreaddy.io/project_files/79c1f631-1cb7-456b-a6cf-fdd8ff21e9a0/49458520-7509-4dcd-8574-b4ec7e4fb656_compressed_C--Image-202695-06_01_15.webp',
    tags: ['安心學習', '學習環境', '古箏教學理念'],
    seoKeywords: ['安心學古箏', '古箏學習環境'],
    content: {
      intro: '什麼樣的學生最終把音樂留在了生活裡？答案是——「學得最安心的那一個」。',
      sections: [
        { heading: '「安心」是一種學習土壤', body: '當一個學生在課堂上感到安全，他才能真正放開來聽、放開來感受，直接說「我不懂」或「我好像練錯了」。這種誠實，才是真正的學習發生的地方。' },
        { heading: '你現在的樣子就夠了', body: '不管你學了幾個月、彈得多或少、這週有沒有練習，你來上課那一天的樣子就是夠的。學習從被接納開始。' },
        { heading: '安心的學習，不代表沒有挑戰', body: '學習本來就有它困難的地方，但這些困難發生在一個安心的環境裡，你遇到它的方式就不同了——不是「又失敗了」，而是「我可以繼續試試看」。' },
        { heading: '長久，才是我在意的事', body: '我不在乎你三個月後能彈幾首曲子，我在乎的是三年後你還願不願意摸箏。' },
      ],
      closing: '如果你也在尋找一個可以安心學習的地方，歡迎加入 LINE@ 聊聊。',
    },
  },
  {
    slug: 'do-i-need-guzheng-before-starting',
    category: 'guzheng-guide',
    categoryLabel: '選箏指南',
    title: '開始學古箏之前，一定要先買箏嗎？',
    excerpt: '「我需要先買古箏才能開始學嗎？」這是很多初學者的第一個問題。答案可能比你想的更輕鬆——不一定。',
    readTime: 5,
    publishedAt: '2025-04-08',
    isFeatured: true,
    coverImage: 'https://storage.helloreaddy.io/project_files/79c1f631-1cb7-456b-a6cf-fdd8ff21e9a0/6eb85f65-a8b0-42c2-96f2-3a429af2ae06_compressed_G_Image_jbga8mjbga8mjbga.webp',
    tags: ['初學者買箏', '需要先買古箏嗎', '選箏諮詢'],
    seoKeywords: ['學古箏需要先買箏嗎', '古箏租借'],
    content: {
      intro: '先了解、先感受，是比先買更重要的事。',
      sections: [
        { heading: '在你決定之前，先試試看', body: '我建議先上幾堂課，感受一下自己和這個樂器的關係，再做購買的決定。衝動買下一把箏，然後發現不太適合，是很常見的事。' },
        { heading: '租借、或在課堂使用練習琴', body: '初學階段可以先使用教室的練習琴，讓你在決定購買之前，有充分的時間了解自己真正需要什麼樣的古箏。' },
        { heading: '什麼時候適合買？', body: '當你上了幾堂課、感受到自己真的想繼續，而且在家有練習的意願和空間時，就是一個好的購買時機。' },
        { heading: '購買前，記得諮詢老師', body: '在決定購買之前，跟老師聊聊你的預算、空間條件與學習目標，能讓你做出更適合自己的選擇。' },
      ],
      closing: '對於選箏有疑問，都歡迎加入 LINE@ 諮詢。',
    },
  },
  {
    slug: 'how-to-choose-guzheng',
    category: 'guzheng-guide',
    categoryLabel: '選箏指南',
    title: '古箏怎麼挑？初學者選箏完整指南',
    excerpt: '第一次選古箏往往讓人不知所措——品牌這麼多、價格差距這麼大、木料名稱也看不懂。這篇文章想幫你把選箏的邏輯理清楚，從容面對這件事。',
    readTime: 8,
    publishedAt: '2025-04-14',
    isFeatured: false,
    coverImage: 'https://storage.helloreaddy.io/project_files/79c1f631-1cb7-456b-a6cf-fdd8ff21e9a0/49cde9a8-9dce-49ac-a054-6e4821595846_compressed_C--Image-202695-11_52_39.webp',
    tags: ['古箏怎麼挑', '選箏指南', '初學古箏推薦'],
    seoKeywords: ['古箏怎麼挑', '初學古箏選什麼'],
    content: {
      intro: '這篇文章想幫你把選箏的邏輯理清楚，讓你能夠有依據地做出選擇，而不是靠運氣。',
      sections: [
        { heading: '首先：不需要買最貴的', body: '初學者需要的是一把音色穩定、手感舒適的練習琴。過早投資昂貴樂器，在學習初期反而感受不到差異。' },
        { heading: '三個初學者最需要考量的選箏指標', body: '第一：音色均勻度。第二：琴弦手感——弦的鬆緊度是否適合你的手指力道。第三：尺寸與空間適配性。' },
        { heading: '網路購買 vs. 實體試彈', body: '強烈建議不要在沒有試彈的情況下網路下單。看圖片和規格參數，完全感受不到一把箏的聲音質感和手感。' },
        { heading: '最好的做法：諮詢老師後再購買', body: '老師能根據你的狀況給出更具體的建議。不要在樂器行被推銷而衝動購買。' },
      ],
      closing: '有選箏上的疑問，都可以加入 LINE@ 聊聊。',
    },
  },
  {
    slug: 'is-guzheng-expensive-myth',
    category: 'guzheng-guide',
    categoryLabel: '選箏指南',
    title: '古箏是不是越貴越好？常見選箏迷思破解',
    excerpt: '「我在想要不要買一把好一點的，這樣學起來比較有動力。」這個想法可以理解，但在選箏這件事上，越貴不一定越好——讓我來解釋為什麼。',
    readTime: 6,
    publishedAt: '2025-05-12',
    isFeatured: false,
    coverImage: 'https://storage.helloreaddy.io/project_files/79c1f631-1cb7-456b-a6cf-fdd8ff21e9a0/6a7290e5-2ebf-411d-a3d8-c28bd3648a2b_compressed_C-Image-202695-04_45_30.webp',
    tags: ['古箏價格', '選箏迷思', '古箏CP值'],
    seoKeywords: ['古箏越貴越好嗎', '古箏多少錢'],
    content: {
      intro: '「越貴越好」在古箏選購上是一個常見誤解。這篇文章想幫你釐清幾個常見的選箏迷思。',
      sections: [
        { heading: '迷思一：初學者感受不到高價箏的差異', body: '高價古箏的優勢在音色的細膩層次上，但這些細節需要一定程度的耳力和技巧才能感受到。初學者使用中等價位的練習琴就已經非常足夠了。' },
        { heading: '迷思二：貴的箏學起來比較有動力', body: '花了很多錢買的箏，往往讓人有「不能不用它」的壓力。如果某週沒時間練習，就會有愧疚感，這種心理負擔有時反而讓人更難持續。' },
        { heading: '適合才是真的好', body: '選箏真正重要的是：這把箏的音色你喜歡嗎？弦的手感適合你的手指嗎？尺寸在你家放得下嗎？' },
        { heading: '什麼時候值得升級？', body: '當你學了一段時間，開始對現在的箏有明確的「不滿足感」——那才是考慮升級的時機。' },
      ],
      closing: '如果你正在考慮要買哪一把箏，都歡迎加入 LINE@ 詢問。',
    },
  },
  {
    slug: 'rent-buy-or-borrow-guzheng',
    category: 'guzheng-guide',
    categoryLabel: '選箏指南',
    title: '租箏、買箏、還是借箏？初學者的三種選擇',
    excerpt: '初學者面對「要不要擁有一把古箏」這個問題，往往不知道哪個選擇最合理。這篇文章從實際面來幫你分析三種方式的優缺點。',
    readTime: 6,
    publishedAt: '2025-05-20',
    isFeatured: false,
    coverImage: 'https://storage.helloreaddy.io/project_files/79c1f631-1cb7-456b-a6cf-fdd8ff21e9a0/b0681812-9ce1-4b01-a7fb-3456dcd80b95_compressed_C--Image-202695-03_35_41-1.webp',
    tags: ['租箏', '買箏', '古箏初學選擇'],
    seoKeywords: ['古箏租借', '初學古箏要買嗎'],
    content: {
      intro: '讓我來幫你把這三個選項都講清楚，讓你能夠根據自己的狀況做出最合適的決定。',
      sections: [
        { heading: '選項一：先在教室使用練習琴', body: '如果你剛開始學、還不確定自己是否會長期繼續，使用教室的練習琴是最低風險的方式。缺點是你在家無法練習。' },
        { heading: '選項二：租借一把回家練', body: '如果你已經確認想繼續學，但還不確定要買哪一把，租借是一個好的過渡方式。費用比購買低，彈性也比較高。' },
        { heading: '選項三：購買一把屬於自己的箏', body: '當你上了幾堂課，確定自己想持續學習，而且在家有空間放箏，這是最好的時機。購買前務必諮詢老師。' },
        { heading: '每一種方式都沒有對錯', body: '最重要的，是找到一個讓你能持續學習的方式，而不是給自己增加不必要的負擔或壓力。' },
      ],
      closing: '對於是否需要有一把自己的箏，都可以加入 LINE@ 直接詢問。',
    },
  },
  {
    slug: 'joining-line-doesnt-mean-signing-up',
    category: 'faq',
    categoryLabel: '常見問題',
    title: '加入 LINE@ 之後，一定要報名嗎？',
    excerpt: '加入 LINE@ 不等於承諾報名。這裡的對話是為了幫助你釐清狀態，不是為了達成成交目標。',
    readTime: 4,
    publishedAt: '2025-04-20',
    isFeatured: false,
    coverImage: 'https://readdy.ai/api/search-image?query=soft%20cozy%20reading%20nook%20with%20warm%20lamp%20light%2C%20open%20notebook%20and%20cup%20of%20herbal%20tea%20on%20wooden%20side%20table%2C%20peaceful%20relaxing%20atmosphere%2C%20no%20pressure%20calm%20environment%2C%20warm%20amber%20and%20cream%20interior%2C%20lifestyle%20photography%20minimal&width=800&height=500&seq=note007&orientation=landscape',
    tags: ['加入LINE@', '不強迫報名', '先了解古箏'],
    seoKeywords: ['加入LINE不用報名', '古箏諮詢聊聊'],
    content: {
      intro: '不會。這篇文章想讓你完全放心地在需要的時候開口問。',
      sections: [
        { heading: '加入 LINE@ 是為了聊聊，不是簽約', body: '這裡的對話從來不是「你有沒有要報名」為起點。我的目標是讓你在對話之後，對自己的狀況有更清楚的認識。' },
        { heading: '聊完之後如果覺得不適合，可以停在那裡', body: '你不需要給任何解釋，也不需要道歉。我希望每個來到這裡的人，都是因為真的覺得適合才開始。' },
        { heading: '什麼樣的問題都可以問', body: '「需要先買箏嗎」「課程費用大概是多少」「我可以先試一堂課嗎」——這些問題都可以直接問。' },
        { heading: '決定永遠由你來做', body: '我能做的是提供你足夠的資訊，讓你做出真正適合自己的選擇。' },
      ],
      closing: '加入 LINE@。門隨時開著，但永遠是你決定要不要走進來。',
    },
  },
  {
    slug: 'how-often-should-i-practice',
    category: 'faq',
    categoryLabel: '常見問題',
    title: '一開始一定要每天練習嗎？練多久才夠？',
    excerpt: '「老師，我一週大概要練幾次？每次要練多久？」這個問題幾乎每個初學者都會問。我的答案可能和你預期的不一樣。',
    readTime: 5,
    publishedAt: '2025-05-08',
    isFeatured: false,
    coverImage: 'https://readdy.ai/api/search-image?query=open%20weekly%20planner%20notebook%20beside%20a%20cup%20of%20tea%20with%20soft%20morning%20light%2C%20planning%20schedule%20concept%20for%20music%20practice%2C%20warm%20and%20gentle%20atmosphere%2C%20minimal%20desk%20scene%20with%20cream%20and%20honey%20tones&width=800&height=500&seq=note018&orientation=landscape',
    tags: ['古箏練習頻率', '每天練古箏', '古箏練多久'],
    seoKeywords: ['古箏要每天練嗎', '古箏練習時間'],
    content: {
      intro: '比你以為的少，但比你現在做的要穩定。',
      sections: [
        { heading: '不需要每天，但需要規律', body: '每週練習三到四次、每次二十到三十分鐘，就已經非常足夠了。規律性比時間長短更重要。' },
        { heading: '把練習的門檻降低', body: '就在你走過它的時候，坐下來，彈幾個音。五分鐘也算。這樣已經很好了。' },
        { heading: '某些週真的太忙怎麼辦？', body: '不需要因此有罪惡感，也不需要下週「補回來」。重要的是在你能夠的時候，讓自己回來。' },
        { heading: '練習的目的，不是完成任務', body: '更好的方式是把練習想成「今天去靠近它一下」——坐下來、調個音、彈幾段你喜歡的部分，就夠了。' },
      ],
      closing: '如果擔心自己沒有時間規律練習，都可以加入 LINE@ 聊聊你的生活節奏。',
    },
  },
  {
    slug: 'difference-between-adult-and-child-learning',
    category: 'faq',
    categoryLabel: '常見問題',
    title: '孩子和成人學古箏，方式有什麼不同？',
    excerpt: '很多家長好奇：孩子和大人一起上課，還是要分開？學習的方式和進度會一樣嗎？這篇文章想讓你對這件事有更清楚的理解。',
    readTime: 6,
    publishedAt: '2025-05-28',
    isFeatured: false,
    coverImage: 'https://storage.helloreaddy.io/project_files/79c1f631-1cb7-456b-a6cf-fdd8ff21e9a0/bea11a4f-ec9f-461e-ab4d-920906da3ac0_compressed_C--Image-202695-04_23_52.webp',
    tags: ['成人學古箏', '兒童學古箏', '學習差異'],
    seoKeywords: ['孩子和成人學古箏差別', '古箏教學年齡'],
    content: {
      intro: '孩子和成人學古箏，學習的起點、動力來源、以及需要的陪伴方式，都有本質上的差異。',
      sections: [
        { heading: '學習動力的來源不同', body: '孩子需要更多外在的引導和正向回饋；成人則往往有更清楚的學習意圖，但也更容易因為「進步太慢」而對自己失去耐心。' },
        { heading: '課堂互動的方式不同', body: '和孩子上課，我會用更多比喻、遊戲化的方式說明；和成人上課，我可以做更多深度的說明——為什麼這個指法要這樣做。' },
        { heading: '在鳴箏，孩子和成人可以共存', body: '我同時教孩子和成人，但通常不會讓他們在同一堂課裡一起上。如果你有孩子，也有自己想學的念頭，都可以來聊聊。' },
        { heading: '找到適合的方式最重要', body: '不管你是家長、還是自己想學的成人，最重要的是找到讓你或孩子感到安心的學習方式。' },
      ],
      closing: '加入 LINE@ 聊聊吧。',
    },
  },
  {
    slug: 'why-i-teach-guzheng-this-way',
    category: 'teacher-notes',
    categoryLabel: '老師筆記',
    title: '為什麼我選擇這樣教古箏',
    excerpt: '我教古箏的方式不像大多數人想像的才藝課——沒有固定進度表、沒有強制考核、不以速度衡量學生。這一切都是有原因的。',
    readTime: 7,
    publishedAt: '2025-04-26',
    isFeatured: true,
    coverImage: 'https://storage.helloreaddy.io/project_files/79c1f631-1cb7-456b-a6cf-fdd8ff21e9a0/6988910a-1c8f-4543-8743-417253d023ba_compressed_d039ecd3-d1bf-43a6-bda7-ac273fa6540d.webp',
    tags: ['鳴箏老師', '教學理念', '陪伴式學習'],
    seoKeywords: ['古箏老師理念', '陪伴式音樂教學'],
    content: {
      intro: '這種教學方式，是我在教學的過程中，慢慢發現、慢慢選擇的結果。',
      sections: [
        { heading: '我曾經用「正確」的方式教學', body: '一開始，我按照一般才藝教學的邏輯：固定的課程內容、明確的進度要求。學生們都在「前進」，但很多人進步了，卻沒有更喜歡古箏。' },
        { heading: '一個學生讓我重新思考', body: '有一位成人學員說「我好像愈來愈不想來了」。不是因為太難，而是有要「表現」的壓力。這讓我開始認真思考：如果學了音樂之後反而更有壓力，那這個學習有什麼意義？' },
        { heading: '音樂應該減壓，而不是增壓', body: '後來我調整了很多。我不再強調每堂課要學到哪裡，而是從學生的狀態出發。有時候課上到一半，我們就只是聊聊這週過得怎樣。' },
        { heading: '選擇慢，是一種對學習者的尊重', body: '快速學完的人有很多，但讓音樂真正成為生活一部分的人，才是我真正想陪伴的。' },
      ],
      closing: '如果你和我對音樂的想法有一些共鳴，歡迎加入 LINE@ 聊聊。',
    },
  },
  {
    slug: 'music-as-quiet-space-in-life',
    category: 'teacher-notes',
    categoryLabel: '老師筆記',
    title: '讓音樂成為生活裡安靜的一角',
    excerpt: '在這個什麼都在搶注意力的時代，能找到一件讓自己安靜下來的事，已經是一種奢侈。古箏對我來說，一直是這樣的存在。',
    readTime: 6,
    publishedAt: '2025-05-05',
    isFeatured: false,
    coverImage: 'https://storage.helloreaddy.io/project_files/79c1f631-1cb7-456b-a6cf-fdd8ff21e9a0/ad585545-6bc9-4c60-9210-3011ba497724_compressed_C--Image-202695-03_35_41.webp',
    tags: ['音樂與生活', '安靜時刻', '古箏陪伴'],
    seoKeywords: ['音樂融入生活', '學古箏放鬆'],
    content: {
      intro: '這篇文章想聊的，是音樂在生活裡可以成為什麼樣的存在。',
      sections: [
        { heading: '那些只屬於練習的二十分鐘', body: '有時候只是坐下來、調一下弦、彈幾個音，就已經讓那一天變得不一樣。那二十分鐘裡，我不需要回覆任何訊息，不需要做任何決定，只需要感受聲音。' },
        { heading: '學生說「我不知道它對我有這麼大的影響」', body: '有幾位學生學著學著說「我發現我壓力很大的時候，會想要彈箏」。當音樂變成了一種需要，它就真的進入生活了。' },
        { heading: '不是每天都要練，但要讓它有位置', body: '音樂不需要每天佔你很多時間，但它需要一個位置。就像一盆植物，不需要時時照顧，但你知道它在那裡。' },
        { heading: '你也可以有這樣的東西', body: '如果你在聽古箏的聲音時，有一種「這個聲音讓我想停下來」的感覺，那也許它正在等你。' },
      ],
      closing: '如果你也想為自己找到這樣一個安靜的角落，加入 LINE@ 聊聊。',
    },
  },
  {
    slug: 'change-i-see-in-students',
    category: 'teacher-notes',
    categoryLabel: '老師筆記',
    title: '我在教學中看見的改變，往往不在音符裡',
    excerpt: '學了一段時間的學生，帶給我最大的驚喜，不是他們彈得更好了，而是他們整個人的狀態變了——某種說不清楚的安定感。',
    readTime: 6,
    publishedAt: '2025-05-22',
    isFeatured: false,
    coverImage: 'https://storage.helloreaddy.io/project_files/79c1f631-1cb7-456b-a6cf-fdd8ff21e9a0/517dda08-1d1c-4a90-b64d-5b4d48ed75d3_compressed_C--Image-202695-11_17_47.webp',
    tags: ['教學觀察', '學生改變', '音樂療癒'],
    seoKeywords: ['學古箏的改變', '古箏對生活的影響'],
    content: {
      intro: '讓我最感動的不是學生學會了哪首曲子，而是他們說：「我最近狀態好多了，我覺得是因為有古箏。」',
      sections: [
        { heading: '改變通常發生在第三個月之後', body: '大約三個月之後，我會看到微妙的轉變：他們進門的時候不再那麼緊張，上課的時候開始會主動說自己想彈什麼。他們開始不只是在「上課」，而是在享受這件事了。' },
        { heading: '有一位學生，開始在睡前彈箏', body: '「不是練習，就是彈一彈，比較容易入睡。」她說的時候帶著一點驚訝。這才是音樂真正的位置——一個讓自己從一天的喧鬧裡退出來的方式。' },
        { heading: '不是古箏改變了他們，而是他們給自己創造了空間', body: '他們開始定期為自己留出一段時間——一段不需要對任何人負責、只需要專注在自己感受的時間。' },
        { heading: '這就是我選擇這樣教學的理由', body: '讓音樂成為一個可以回去的地方，而不是另一個需要表現的場合。' },
      ],
      closing: '如果你也在找一個可以讓自己慢下來的事，可以先加入 LINE@ 聊聊。',
    },
  },
  {
    slug: 'about-comparison-and-companionship',
    category: 'teacher-notes',
    categoryLabel: '老師筆記',
    title: '關於學習，我相信的不是比較，而是陪伴',
    excerpt: '在我的教學裡，我刻意不做一件事：比較。不比學生之間，也不讓學生和「應該達到的標準」比較。這不是因為我不在乎進步，而是因為我見過比較帶來的傷害。',
    readTime: 6,
    publishedAt: '2025-06-10',
    isFeatured: false,
    coverImage: 'https://readdy.ai/api/search-image?query=two%20people%20sitting%20together%20companionably%20beside%20a%20guzheng%20in%20warm%20afternoon%20light%2C%20sense%20of%20quiet%20companionship%20and%20mutual%20understanding%2C%20warm%20beige%20interior%2C%20soft%20natural%20light%2C%20lifestyle%20documentary%20photography&width=800&height=500&seq=note021&orientation=landscape',
    tags: ['陪伴式教學', '不比較', '鳴箏理念'],
    seoKeywords: ['陪伴式音樂學習', '古箏學習心態'],
    content: {
      intro: '比較，比任何技術上的困難都更傷害學習。這篇文章想聊聊為什麼我選擇了陪伴作為替代。',
      sections: [
        { heading: '比較是從哪裡來的？', body: '我們從小就在比較的環境裡長大。這種習慣帶進音樂學習裡，會讓你無法真正感受當下的聲音，因為你一直在評估自己是不是夠好。' },
        { heading: '陪伴，是另一種前進的方式', body: '陪伴的意思是：我在你旁邊，我看見你現在在哪裡，我不要你假裝你在另一個地方。當你感覺到「不管我現在的狀態是什麼，這裡都接受我」，你才能真正把注意力放在音樂本身。' },
        { heading: '學習裡的「停留」，也是一種進步', body: '有時候學生在同一個段落上停留很久，我不急著讓他翻篇，我陪他在那裡。那些「停留」往往是最深的學習正在發生的時候。' },
        { heading: '你的節奏就是你的節奏', body: '沒有哪種學習節奏是「正確」的。我只是在這裡陪著你，不論你現在走在哪一段路上。' },
      ],
      closing: '如果你也想要一個不用比較、可以安心學習的地方，歡迎加入 LINE@ 聊聊。',
    },
  },
];

export const getFeaturedArticles = () => noteArticles.filter((a) => a.isFeatured);
export const getArticlesByCategory = (category: string) => noteArticles.filter((a) => a.category === category);
export const getNonFeaturedArticlesByCategory = (category: string) => noteArticles.filter((a) => a.category === category && !a.isFeatured);
export const getArticleBySlug = (slug: string) => noteArticles.find((a) => a.slug === slug);
export const getRelatedArticles = (slug: string, count = 3): NoteArticle[] => {
  const current = getArticleBySlug(slug);
  if (!current) return noteArticles.filter((a) => a.slug !== slug).slice(0, count);
  const sameCategory = noteArticles.filter((a) => a.slug !== slug && a.category === current.category);
  const others = noteArticles.filter((a) => a.slug !== slug && a.category !== current.category);
  const othersFeatured = others.filter((a) => a.isFeatured);
  const othersRest = others.filter((a) => !a.isFeatured);
  return [...sameCategory, ...othersFeatured, ...othersRest].slice(0, count);
};
